import React, {
  FC,
  useState,
  useEffect,
  useContext,
  createContext,
  useCallback,
} from "react";
import { useAuth } from "../auth/AuthContext";
import { Kiosk } from "../../../domains/kiosk/aggregates/Kiosk";
import { MeepOrderRepository } from "../../../Infrastructure/repositories/api/OrderRepository";
import { KioskRepository } from "../../../Infrastructure/repositories/api/KioskRepository";
import { getGlobalAddLog } from "Infrastructure/repositories/appInsights/AppInsights";
import { PrintType } from "domains/kiosk/aggregates/PrintType";
import { isReactNative } from "utils/constants";
import { getOrderRepository } from "Infrastructure/repositories/localStorage/getOrderRepository";
import { CashlessRepository } from "Infrastructure/repositories/api/CashlessRepository";
import { ProcessPendingOnlineRechargesUseCase } from "modules/order/application/useCases/PendingOnlineRechargeQueue";

const addLog = getGlobalAddLog();

interface IKioskContext {
  kiosk: Kiosk | null;
  syncKiosk: () => Promise<Kiosk | null | undefined>;
}

const Context = createContext<IKioskContext>({} as IKioskContext);

//container
const kioskRepository = KioskRepository();
const orderRepository = MeepOrderRepository();
const localOrderRepository = getOrderRepository();
const cashlessRepository = CashlessRepository();

const PENDING_RECHARGE_RETRY_MS = 2 * 60 * 1000;


export const KioskProvider: FC = ({ children }) => {
  const { isAuth } = useAuth();

  const [kiosk, setKiosk] = useState<Kiosk | null>(null);

  // Recargas online pagas que não chegaram ao backend (a retomada de pagamento m-SiTef perdido
  // fica no PaymentRecovery, que precisa do OrderProvider e das rotas)
  useEffect(() => {
    if (!kiosk || !isAuth || !isReactNative) {
      return;
    }
    const processRecharges = () =>
      ProcessPendingOnlineRechargesUseCase(localOrderRepository, orderRepository, cashlessRepository, kiosk).catch(
        (error) => addLog && addLog("PendingOnlineRecharge", { error })
      );
    processRecharges();
    const interval = setInterval(processRecharges, PENDING_RECHARGE_RETRY_MS);
    return () => clearInterval(interval);
  }, [kiosk, isAuth]);

  useEffect(() => {
    if (kiosk && isAuth) {
      try {
        orderRepository.syncOrdersAsync(kiosk?.localId, kiosk?.id);
      } catch (error) {
        addLog && addLog("Kiosk", { error });
      }
    }
  }, [kiosk, isAuth]);

  const interceptorKiosk = useCallback((kiosk: Kiosk | null) => {
    if (
      kiosk?.localId.toUpperCase() ===
      "5650DE25-2E2A-0EAB-AC51-B5F554BC2908".toUpperCase()
    ) {
      return {
        ...kiosk,
        enableInstallment: true,
      };
    }
    if (
      kiosk?.localId.toUpperCase() ===
      "24c93427-8517-44fb-866d-d4f13662feb7".toUpperCase()
    ) {
      return {
        ...kiosk,
        enableInstallment: true,
      };
    }
    if (
      kiosk?.localId.toUpperCase() ===
      "f917eaa8-0f80-23e4-e331-f7bb45b185cc".toUpperCase()
    ) {
      return {
        ...kiosk,
        enableCpfRegister: true,
      };
    }
    if (
      kiosk?.localId.toUpperCase() ===
      "f536a9b8-e985-b340-8204-77d82f8d0ed1".toUpperCase()
    ) {
      //beco do espeto
      return {
        ...kiosk,
        oldPrinter: true,
      };
    }
    if (
      kiosk?.localId.toUpperCase() ===
      "ef4990aa-cef3-f646-0bec-2ecd30796fe6".toUpperCase()
    ) {
      //CAVALHADA DE SANTO ANTONIO
      return {
        ...kiosk,
        printType: PrintType.TICKET,
      };
    }

    if (
      kiosk?.localId.toUpperCase() ===
      "e812c55a-55f8-0a47-c4e7-d7137a1ae59d".toUpperCase() //escola deivid
    ) {
      //escola deivid
      return {
        ...kiosk,
        disableDoubleFacialValidation: true,
      };
    }

    return kiosk;
  }, []);

  const syncKiosk = useCallback(async () => {
    try {
      const updatedKiosk = await kioskRepository.syncAsync();
      setKiosk(interceptorKiosk(updatedKiosk));
      return updatedKiosk;
    } catch (error) {
      addLog && addLog("Login", { error });
      return undefined;
    }
  }, [interceptorKiosk]);

  useEffect(() => {
    if (isAuth) {
      syncKiosk();
    }
  }, [isAuth, syncKiosk]);

  useEffect(() => {
    kioskRepository.getAsync().then((kiosk) => {
      setKiosk(interceptorKiosk(kiosk));
    });
  }, [interceptorKiosk]);

  return (
    <Context.Provider value={{ kiosk, syncKiosk }}>
      {children}
    </Context.Provider>
  );
};

export const KioskConsumer = Context.Consumer;

export const useKiosk = () => {
  return useContext(Context);
};
